import useStore from "../../../../helpers/useStore";
import { PatientsContext } from "../../../../stores/Patients/patients.provider";
import { usePatientBillingQueryData } from "../hooks/usePatientBillingQuery";
import { PaymentDialogType, usePatientBillingActions } from "../hooks/usePatientBillingActions";
import { Backdrop, Box, CircularProgress, Typography, Grid } from "@mui/material";
import { TopLevelPaper } from "../../../../themes/StyledComponents";
import PermissionCard from "./DebitPermission/PermissionCard";
import CreditPermissionCard from "./CreditPermission/CreditPermissionCard";
import SepaBankAccountsCard from "../sepa/SepaBankAccountsCard";
import EinmalLastschriftDialog from "../sepa/EinmalLastschriftDialog";
import { PatientBillInfoDto } from "../../../../api-client";
import { useState } from "react";
// import NewPatientRechnungTable from "../tables/NewPatientRechnungTable";
// import PatientAllTransactionTable from "../tables/PatientAllTransactionTable";
import { useLocationId, useUserId } from "../../../../stores/User/User.hooks";
// import NewPatientBillTable from "../tables/NewPatientBillTable";
import NewAlertDialogSlide from "../../../Transactions/Kasse/forms/NewTransactionMethod";
import { observer } from "mobx-react";
import NewPayToPatientDialog from "../dialogs/NewPayToPatientDialog";
import { NewExternalBalanceDialog } from "../dialogs/NewExternalBalanceDialog";
import NewTransactionForm from "../../../Transactions/Kasse/forms/NewTransactionForm";
import { transactionMethods } from "../../../Transactions/Kasse/v2/types/kasse.types";
import TransactionTable from "../tables/TransactionTable";
import RxPriceTable from "../tables/RxPriceTable";
import RechnungTable from "../tables/RechnungTable";

const PatientBilling = observer(() => {
  /**
   * Usage of Stores:
   * 1. PatientStore: To get the selected patient ID.
   */
  const PatientStore = useStore(PatientsContext);
  const { selectedPatientId } = PatientStore;

  /**
   * This hook is defined in the store/User/User.hooks.ts
   * It provides the current logged-in user's ID.
   */
  const userId = useUserId();
  const locationId = useLocationId();

  /**
   * Usage of Query Data:
   * This custom hook encapsulates all the data fetching logic related to patient billing.
   * It fetches bills, transactions, rechnungen, and debit permissions in parallel.
   * It also constructs a rechnungMap for efficient access.
   * The hook returns loading states during fetching.
   */
  const {
    billsInfo,
    allTransactions,
    rechnungBills,
    debitPermissions,
    creditPermissions,
    rechnungMap,
    isLoading,
  } = usePatientBillingQueryData(selectedPatientId!);

  /**
   * Whether the patient has a billing email (alternative-billing → primary fallback,
   * resolved on the backend). Used to default the "Mit Briefkopf" toggle.
   */
  const patientHasBillingEmail = billsInfo[0]?.patientHasBillingEmail;

  /**
   * Usage of Actions:
   * This custom hook encapsulates all the action handlers related to patient billing.
   * It uses mutations and local state to manage dialogs and actions.
   */
  const {
    dialog,
    closeDialogs,
    handlePay,
    handlePaybackToPatient,
    handleLastschrift,
    handleReturnToPatient,
    handleCreateRechnung,
    handleExternalBill,
    handleCreatePatientTransaction,
    handleSepaButtonClick,
    handleSepaSelect,
    closeSepaMenu,
    sepaMenuState,
    withLetterhead,
    setWithLetterhead,
    isRowBusy,
    busy,
  } = usePatientBillingActions({
    patientId: selectedPatientId!,
    patientHasBillingEmail,
  });

  const globalBusy = isLoading || busy;

  /**
   * Top-level state for the new SEPA one-off ("Einmal Lastschrift") flow. It is
   * launched from inside the "Zahlen" modal but rendered here at the top level so the
   * shadcn dialog is not nested inside the MUI modal (avoids z-index conflicts).
   */
  const [einmalBill, setEinmalBill] = useState<PatientBillInfoDto | null>(null);

  return (
    <>
      <Backdrop open={globalBusy} sx={{ color: "#fff", zIndex: (t) => t.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
        &nbsp; Bitte warten...
      </Backdrop>

      <Grid container spacing={2}>
        {/* LEGACY debit/credit permission cards — replaced by the SEPA rebuild
            (SepaBankAccountsCard). Kept commented for reference during rollout.
        <Grid item xs={12} md={6}>
          <PermissionCard
            patientData={selectedPatientId!}
            debitPermissions={debitPermissions ?? []}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CreditPermissionCard
            patientData={selectedPatientId!}
            creditPermissions={creditPermissions ?? []}
          />
        </Grid>
        */}
        <Grid item xs={12}>
          <SepaBankAccountsCard patientId={selectedPatientId!} />
        </Grid>
      </Grid>

      <RxPriceTable
        billsInfo={billsInfo}
        rechnungMap={rechnungMap}
        onClickPay={handlePay}
        onClickPaybackToPatient={handlePaybackToPatient}
        onClickBill={handleCreateRechnung}
        onClickLastschrift={handleLastschrift}
        onResumeEinmal={setEinmalBill}
        onHandleSepaButtonClick={handleSepaButtonClick}
        onHandleSepaSelect={handleSepaSelect}
        sepaMenuState={sepaMenuState}
        handleExternalBill={handleExternalBill}
        isRowBusy={isRowBusy}
        debitPermissions={debitPermissions ?? []}
        onCloseSepaMenu={closeSepaMenu}
        withLetterhead={withLetterhead}
        onToggleLetterhead={setWithLetterhead}
      />

      <RechnungTable rechnungen={rechnungBills} patientId={selectedPatientId!} />
      <TransactionTable allTransactions={allTransactions} />

      {dialog.type === PaymentDialogType.PAY && (
        <NewAlertDialogSlide
          open
          handleClose={() => {
            closeDialogs();
          }}
          patientId={selectedPatientId!}
          rxId={dialog.rxId}
          onSelectEinmal={() => {
            const bill = billsInfo.find((b) => b.rxId === dialog.rxId) ?? null;
            closeDialogs();
            setEinmalBill(bill);
          }}
          handleCreatePatientTransaction={handleCreatePatientTransaction}
        />
      )}

      {einmalBill && (
        <EinmalLastschriftDialog
          bill={einmalBill}
          patientId={selectedPatientId!}
          open
          onOpenChange={(o) => {
            if (!o) setEinmalBill(null);
          }}
        />
      )}

      {dialog.type === PaymentDialogType.DEBIT && (
        <NewTransactionForm
          open
          handleTransactionClose={closeDialogs}
          patientId={selectedPatientId!}
          rxId={dialog.rxId}
          debitPermission={dialog.permission}
          transactionMethod={transactionMethods.LASTSCHRIFT}
          handleCreatePatientTransaction={handleCreatePatientTransaction}
        />
      )}

      {dialog.type === PaymentDialogType.RETURNTOPATIENT && (
        <NewPayToPatientDialog
          open
          handleReturnToPatient={handleReturnToPatient}
          patientId={selectedPatientId!}
          rxId={dialog.rxId}
          userId={userId ?? ""}
          locationId={locationId ?? ""}
          onPaymentSuccess={() => {
            closeDialogs();
          }}
        />
      )}
      {dialog.type === PaymentDialogType.EXTERNALBILL && (
        <NewExternalBalanceDialog
          open
          userId={userId ?? ""}
          patientId={selectedPatientId!}
          rxId={dialog.rxId}
          onPaymentSuccess={() => {
            closeDialogs();
          }}
        />
      )}
    </>
  );
});

export default PatientBilling;
